import {
    useAuth0 as useAuth0Hook,
    User as Auth0User,
    Auth0ContextInterface,
    PopupConfigOptions,
} from '@auth0/auth0-react'
import Cookies from 'js-cookie'
import { useCallback, useEffect, useRef, useState } from 'react'
import AggregateError from 'core-js/features/aggregate-error'
import { BroadcastChannel } from 'broadcast-channel'

import { Environment } from 'data/Environment'
import { User } from 'data/User'
import store from 'libs/web-client/utils/auth0/src/redux/store'
import {
    callbacksReceived,
    getLoginPopup,
    getLoginPopupWindow,
    resetLoginPopup,
    setLoginPopup,
} from 'libs/web-client/utils/auth0/src/redux/modules/auth0/auth0.slice'
import { translateAuth0User } from 'libs/web-client/utils/auth0/src/util/user'
import { getConfig } from './config'
import useIsMounted from 'hooks/use-is-mounted'
import {
    AUTH0_ERROR,
    AUTH_BROADCAST_CHANNEL_NAME,
    EXISTING_AUTH_COOKIE_NAME,
    EXISTING_AUTH_COOKIE_VALUE,
} from '../constants'

type AuthErrorHandler = (error: AggregateError) => void

export class AuthTimeoutError extends Error {}

const TIMEOUT_WINDOW = 5000

type AuthenticatedState = {
    user: User
    isAuthenticated: Auth0ContextInterface['isAuthenticated']
}

enum AUTH_EVENT {
    LOGIN_STATE_CHANGE,
}

type ChannelMessage = {
    event: AUTH_EVENT
    origin: string
}

// construct unique id for current browser context (i.e. tab/window)
const context_id = 'auth-tab-' + Math.floor(Math.random() * 1000000)

const getAuthCookieState = () => Cookies.get(EXISTING_AUTH_COOKIE_NAME) === EXISTING_AUTH_COOKIE_VALUE

/**
 * Wrapper around useAuth0 from auth0-react.
 *
 * @returns {Auth0ContextInterface} Output from wrapped useAuth0 with some custom logic
 */
export const useAuth0 = (): Auth0ContextInterface => {
    const { environment } = getConfig()
    const authTimeout = parseInt(((environment as Environment).auth0 as Environment).timeout as string) / 1000

    const {
        getAccessTokenSilently,
        isAuthenticated,
        error,
        isLoading: auth0Loading,
        loginWithPopup: auth0LoginWithPopup,
        logout: auth0Logout,
        user,
        ...auth0Rest
    } = useAuth0Hook()
    const isLoading = getAuthCookieState() && auth0Loading

    const loginWithPopup = useCallback(
        (options, config: PopupConfigOptions = {}) => {
            if (!config.popup) {
                const width = 300
                const height = 580
                const left = window.screenX + (window.innerWidth - width) / 2
                const top = window.screenY + (window.innerHeight - height) / 2

                config.popup = window.open(
                    '',
                    'auth0:authorize:popup',
                    `left=${left},top=${top},width=${width},height=${height},resizable,scrollbars=yes,status=1`,
                )
            }

            config.popup.focus()

            store.dispatch(setLoginPopup({ popup: config.popup }))

            if (!config.timeoutInSeconds) {
                config.timeoutInSeconds = authTimeout
            }

            return auth0LoginWithPopup(options, config)
        },
        [authTimeout, auth0LoginWithPopup],
    )

    const logoutRedirect = ((environment as Environment).auth0 as Environment).logoutRedirect as string
    const logout = useCallback(() => auth0Logout({ returnTo: logoutRedirect }), [logoutRedirect, auth0Logout])

    useEffect(() => {
        const getIsAuthenticated = () => isAuthenticated
        store.dispatch(resetLoginPopup())
        store.dispatch(callbacksReceived({ getAccessTokenSilently, isAuthenticated: getIsAuthenticated, logout }))
    }, [getAccessTokenSilently, isAuthenticated, logout])

    return { ...auth0Rest, error, getAccessTokenSilently, isAuthenticated, isLoading, loginWithPopup, logout, user }
}

export const useSso = () => {
    // track mounted state for effect cleanup
    const isMounted = useIsMounted()

    // open up a channel for communicating between tabs
    const broadcastChannel = useRef(new BroadcastChannel(AUTH_BROADCAST_CHANNEL_NAME))

    const { isAuthenticated, isLoading: auth0Loading, getAccessTokenSilently, logout } = useAuth0()

    const [isLoadingInternal, setLoading] = useState(true)

    const [authChanged, setAuthChanged] = useState(0)

    const isLoading = auth0Loading || isLoadingInternal

    // subscribe to broadcast auth messages
    useEffect(() => {
        const handler = (msg: ChannelMessage) => {
            // if this came from us, ignore it
            if (msg.origin === context_id) {
                return
            }
            // handle event
            if (msg.event === AUTH_EVENT.LOGIN_STATE_CHANGE) {
                setAuthChanged(Math.random())
            }
        }
        broadcastChannel.current.addEventListener('message', handler)
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
        return () => broadcastChannel.current.removeEventListener('message', handler)
    }, [broadcastChannel, setAuthChanged])

    useEffect(() => {
        // if we think we're NOT logged in, but we ARE
        if ((!isAuthenticated && !getAuthCookieState()) || (authChanged && getAuthCookieState())) {
            // login
            /* eslint-disable-next-line @typescript-eslint/no-extra-semi */
            ;(async () => {
                try {
                    await getAccessTokenSilently()
                } catch (e) {
                    /* eslint-disable-next-line no-console*/
                    console.debug(`Login check: not logged in (${e.constructor.name}: ${e.message}) `)
                } finally {
                    if (isMounted()) {
                        setLoading(false)
                    }
                }
            })()
        } else {
            setLoading(false)
        }
        // if we think we ARE logged in, but we're NOT
        if (isAuthenticated && !getAuthCookieState()) {
            // logout
            logout()
        }
    }, [getAccessTokenSilently, isMounted, logout, isAuthenticated, authChanged])

    useEffect(() => {
        broadcastChannel.current.postMessage({
            event: AUTH_EVENT.LOGIN_STATE_CHANGE,
            origin: context_id,
        } as ChannelMessage)
    }, [broadcastChannel, isAuthenticated])

    return { isLoading }
}

export const useErrorHandler = (onError: AuthErrorHandler) => {
    const { environment } = getConfig()
    const authTimeout = parseInt(((environment as Environment).auth0 as Environment).timeout as string) / 1000

    const { error } = useAuth0Hook()

    useEffect(() => {
        // if we have no error, OR if we weren't given an error handler, do nothing
        if (!error) {
            return
        }
        // else, we have an error
        let message
        const errors = [error]
        // if this is a popup closed error
        if (error.message === AUTH0_ERROR.POPUP_CLOSED) {
            // this is a stupid error
            /* eslint-disable-next-line no-console*/
            console.debug('Ignoring auth error: ', error)
            return
        }
        // if this is a timeout error
        if (error.message === AUTH0_ERROR.TIMEOUT) {
            // check and see if we currently have a popup open that was opened
            // at approximately the start of (or earlier than) the timeout
            const loginPopup = getLoginPopup(store.getState())
            const loginWindow = getLoginPopupWindow(store.getState())
            const now = Date.now()
            if (
                loginPopup &&
                loginWindow &&
                !loginWindow.closed &&
                now - loginPopup.openTime.getTime() >= authTimeout * 1000 - TIMEOUT_WINDOW
            ) {
                // this is a bona-fide timeout
                const timeoutError = new AuthTimeoutError(
                    `Encountered timeout error after configured timeout of ${authTimeout} seconds.`,
                )
                /* eslint-disable-next-line no-console*/
                console.warn(timeoutError.message, error)
                message = 'Your session has expired. Please try again.'
                errors.unshift(timeoutError)
                // if we've timed out, then we should start over again (window won't communicate with parent)
                loginWindow.close()
                store.dispatch(resetLoginPopup())
            } else {
                // else, we'll safely ignore this error
                /* eslint-disable-next-line no-console*/
                console.debug('Ignoring auth error: ', error)
                return
            }
        } else {
            console.error('Authentication error: ', error)
            message = 'Your user session had a hiccup. If this continues to happen, try logging out and back in.'
        }
        // pass error to handler
        onError(new AggregateError([error], message))
    }, [error, onError, authTimeout])

    return { error }
}

/**
 *
 * Returns the user from Auth0 translated into a user that is used internally
 * in our applications.
 *
 * @throws This method should only be called if the user is guaranteed to be
 * authenticated and will throw an error if the user is NOT authenticated.
 *
 * @returns {User} The authenticated user
 */
export const useInternalUser = ({ suppressErrors = false } = {}): User => {
    const { isAuthenticated, user: auth0User } = useAuth0()
    const { environment } = getConfig()

    if (!isAuthenticated && !suppressErrors) {
        throw new Error('useInternalUser() called but user not authenticated!')
    }

    const translate = useCallback(
        () => (isAuthenticated ? translateAuth0User(auth0User as Auth0User, environment as Environment) : null),
        [isAuthenticated, auth0User, environment],
    )

    return translate() as User
}

/**
 * Returns whether or not the user is authenticated. If the user IS
 * authenticated, then the user will also be returned.
 *
 * If NOT authenticated, the user will be `null`, and should only be accessed
 * if `isAuthenticated` is true. The TypeScript `type` of `user` is assumed to
 * be `User`.
 *
 * @returns {AuthenticatedState} - The authenticated state of the user
 */
export const useAuthenticatedState = (): AuthenticatedState => {
    const { isAuthenticated } = useAuth0()
    const user = useInternalUser({ suppressErrors: true })
    return { isAuthenticated, user }
}
